import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Clock, FileText, ClipboardList, CheckCircle, AlertCircle, Trophy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAM_TYPE_LABEL = {
  unit: 'Unit Test',
  quarterly: 'Quarterly',
  half_yearly: 'Half-Yearly',
  annual: 'Annual',
};

const StudentMockExams = ({ onBack }) => {
  const [view, setView] = useState('list'); // list | exam | result
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeExam, setActiveExam] = useState(null); // mock_exam object
  const [activeResult, setActiveResult] = useState(null);
  const [disputing, setDisputing] = useState({}); // {questionId: true} during API call

  const disputeAiGrade = async (questionId) => {
    if (!activeResult?.id) return;
    if (!window.confirm("Send this answer to your teacher for a second look? Your AI score may go up or stay the same — it won't go down.")) return;
    setDisputing(p => ({ ...p, [questionId]: true }));
    try {
      await axios.post(`${API}/student/mock-exam-attempts/${activeResult.id}/dispute/${questionId}`);
      toast.success('Sent to teacher for review');
      // Optimistically update local state
      setActiveResult(prev => ({
        ...prev,
        manual_review_pending: true,
        per_question_results: (prev.per_question_results || []).map(r =>
          r.question_id === questionId ? { ...r, disputed: true, status: 'needs_review' } : r
        ),
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send dispute');
    } finally {
      setDisputing(p => ({ ...p, [questionId]: false }));
    }
  };

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/student/mock-exams`);
      setExams(res.data.mock_exams || []);
    } catch (err) {
      toast.error('Failed to load mock exams');
    } finally { setLoading(false); }
  };

  const startExam = async (mockId) => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/student/mock-exams/${mockId}/start`);
      setActiveExam(res.data.mock_exam);
      setView('exam');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start');
    } finally { setLoading(false); }
  };

  const viewResult = async (mockId) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/student/mock-exams/${mockId}/result`);
      setActiveResult(res.data);
      setView('result');
    } catch (err) {
      toast.error('Failed to load result');
    } finally { setLoading(false); }
  };

  if (view === 'exam' && activeExam) {
    return (
      <ExamRunner
        mock={activeExam}
        onBack={() => { setActiveExam(null); setView('list'); load(); }}
        onSubmitted={(result) => { setActiveResult(result.attempt); setView('result'); load(); }}
      />
    );
  }

  if (view === 'result' && activeResult) {
    return <ResultView attempt={activeResult} onBack={() => { setActiveResult(null); setView('list'); load(); }} />;
  }

  return (
    <div className="space-y-4">
      <Button variant="outline" size="sm" onClick={onBack} data-testid="back-from-mock-exams">
        <ArrowLeft size={16} className="mr-1" /> Back
      </Button>

      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl p-4 md:p-6 shadow-sm">
        <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2">
          <ClipboardList size={26} />
          Class Exams
        </h2>
        <p className="text-indigo-100 mt-1 text-sm">Practice full exam papers in real format — Unit, Quarterly, Half-Yearly & Annual. AI-graded with model answers. <strong>For practice only — not used for report cards.</strong></p>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-8 text-center"><Loader2 className="animate-spin mx-auto text-indigo-500" /></div>
      ) : exams.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500">
          <FileText className="mx-auto mb-2 text-gray-300" size={48} />
          <p>No mock exams published yet.</p>
          <p className="text-xs mt-1">Your teacher will publish exams here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {exams.map(m => (
            <div key={m.id} className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-2" data-testid={`student-mock-card-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <Badge variant="secondary" className="mb-2">{EXAM_TYPE_LABEL[m.exam_type]}</Badge>
                  <p className="font-semibold text-gray-800 text-sm">{m.title}</p>
                  <p className="text-xs text-gray-500 mt-1">{m.board} · {m.total_marks} marks · {m.duration_minutes} min</p>
                </div>
                {m.attempt_status === 'submitted' && <Trophy className="text-yellow-500" size={20} />}
              </div>
              {m.attempt_status === 'submitted' ? (
                <div className="flex items-center justify-between gap-2 mt-2">
                  <div className="text-sm">
                    <span className="font-bold text-indigo-700">{m.auto_score} / {m.total_marks}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => viewResult(m.id)} data-testid={`view-result-${m.id}`}>View Result</Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  onClick={() => startExam(m.id)}
                  className="bg-indigo-600 hover:bg-indigo-700 mt-1"
                  data-testid={`start-exam-${m.id}`}
                >
                  Start Exam
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== EXAM RUNNER ==============
const ExamRunner = ({ mock, onBack, onSubmitted }) => {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState((mock.duration_minutes || 60) * 60);

  // Flatten questions for navigation
  const allQuestions = [];
  mock.sections.forEach((sec, si) => {
    sec.questions.forEach((q, qi) => {
      allQuestions.push({ ...q, sectionIndex: si, sectionName: sec.name, sectionType: sec.type, markPerQ: sec.marks_per_question });
    });
  });

  useEffect(() => {
    if (timeLeft <= 0) { handleSubmit(true); return; }
    const t = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60); const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const handleAnswer = (qid, value) => {
    setAnswers(prev => ({ ...prev, [qid]: value }));
  };

  const handleSubmit = async (auto = false) => {
    if (!auto && !window.confirm('Submit your exam now?')) return;
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/student/mock-exams/${mock.id}/submit`, { answers });
      toast.success(`Submitted! Auto-score: ${res.data.auto_score}/${res.data.total_marks}`);
      const resultRes = await axios.get(`${API}/student/mock-exams/${mock.id}/result`);
      onSubmitted({ attempt: resultRes.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-3 md:p-4 sticky top-0 z-10 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-bold text-gray-800 text-sm md:text-base">{mock.title}</h2>
          <p className="text-xs text-gray-500">{mock.total_marks} marks · {allQuestions.length} questions</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1 px-3 py-1 rounded-lg ${timeLeft < 300 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
            <Clock size={16} />
            <span className="font-mono font-bold text-sm" data-testid="exam-timer">{formatTime(timeLeft)}</span>
          </div>
          <Button size="sm" onClick={() => handleSubmit(false)} disabled={submitting} className="bg-green-600 hover:bg-green-700" data-testid="submit-exam-btn">
            {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Submit
          </Button>
        </div>
      </div>

      {mock.sections.map((sec, si) => (
        <div key={si} className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-3 bg-gray-50 border-b">
            <h3 className="font-semibold text-gray-800 text-sm">{sec.name}</h3>
            <p className="text-xs text-gray-500">{sec.questions.length} questions × {sec.marks_per_question} mark{sec.marks_per_question > 1 ? 's' : ''}</p>
          </div>
          <div className="p-3 space-y-4">
            {sec.questions.map((q) => (
              <div key={q.id} className="border-b pb-3 last:border-0">
                <p className="text-sm font-medium text-gray-800 mb-2">
                  Q{q.question_number || ''}. {q.question}
                  <span className="text-xs text-gray-500 font-normal ml-2">({q.marks} mark{q.marks > 1 ? 's' : ''})</span>
                </p>
                {sec.type === 'mcq' && q.options && (
                  <div className="space-y-1.5 ml-2">
                    {Object.entries(q.options).map(([k, v]) => (
                      <label key={k} className={`flex items-start gap-2 p-2 rounded cursor-pointer text-sm ${answers[q.id] === k ? 'bg-indigo-50 border border-indigo-300' : 'hover:bg-gray-50 border border-transparent'}`}>
                        <input
                          type="radio"
                          name={q.id}
                          value={k}
                          checked={answers[q.id] === k}
                          onChange={() => handleAnswer(q.id, k)}
                          className="mt-0.5"
                          data-testid={`mcq-option-${q.id}-${k}`}
                        />
                        <span><strong>{k}.</strong> {v}</span>
                      </label>
                    ))}
                  </div>
                )}
                {sec.type === 'short_answer' && (
                  <Textarea
                    value={answers[q.id] || ''}
                    onChange={e => handleAnswer(q.id, e.target.value)}
                    placeholder="Type your short answer..."
                    rows={2}
                    className="text-sm"
                    data-testid={`short-answer-${q.id}`}
                  />
                )}
                {sec.type === 'descriptive' && (
                  <Textarea
                    value={answers[q.id] || ''}
                    onChange={e => handleAnswer(q.id, e.target.value)}
                    placeholder="Type your detailed answer..."
                    rows={4}
                    className="text-sm"
                    data-testid={`descriptive-answer-${q.id}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="bg-white rounded-xl p-4 shadow-sm flex justify-between gap-2">
        <Button variant="outline" onClick={onBack}>Cancel</Button>
        <Button onClick={() => handleSubmit(false)} disabled={submitting} className="bg-green-600 hover:bg-green-700">
          {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Submit Exam
        </Button>
      </div>
    </div>
  );
};

// ============== RESULT VIEW ==============
const ResultView = ({ attempt, onBack }) => {
  const pct = attempt.percentage || (attempt.total_marks ? Math.round((attempt.auto_score / attempt.total_marks) * 100) : 0);
  const grade = pct >= 90 ? 'A+' : pct >= 80 ? 'A' : pct >= 70 ? 'B+' : pct >= 60 ? 'B' : pct >= 50 ? 'C' : pct >= 35 ? 'D' : 'F';
  return (
    <div className="space-y-4">
      <Button variant="outline" size="sm" onClick={onBack}><ArrowLeft size={16} className="mr-1" /> Back</Button>

      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white rounded-2xl p-6 shadow-md text-center">
        <Trophy className="mx-auto mb-2 text-yellow-300" size={48} />
        <p className="text-sm uppercase tracking-wide opacity-80">Auto-Score</p>
        <p className="text-5xl font-bold">{attempt.auto_score} / {attempt.total_marks}</p>
        <p className="text-3xl font-semibold mt-2">{pct}% · Grade {grade}</p>
        {attempt.manual_review_pending && (
          <p className="mt-3 text-xs bg-yellow-400/30 inline-block px-3 py-1 rounded-full">
            Some answers pending teacher review — final score may improve.
          </p>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-3 bg-gray-50 border-b">
          <h3 className="font-semibold text-gray-800">Question-by-Question Review</h3>
        </div>
        <div className="divide-y max-h-[600px] overflow-y-auto">
          {(attempt.per_question_results || []).map((r, i) => {
            const color = r.status === 'correct' ? 'bg-green-50 border-l-green-500'
              : r.status === 'partial' ? 'bg-yellow-50 border-l-yellow-500'
              : r.status === 'incorrect' ? 'bg-red-50 border-l-red-500'
              : r.status === 'needs_review' ? 'bg-blue-50 border-l-blue-500'
              : r.status === 'ai_graded' ? 'bg-indigo-50 border-l-indigo-500'
              : 'bg-gray-50 border-l-gray-300';
            return (
              <div key={i} className={`p-3 border-l-4 ${color}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="text-sm font-semibold flex-1">Q{i + 1}. {r.question}</p>
                  <Badge variant="outline" className="shrink-0 text-xs">
                    {r.awarded} / {r.marks}
                  </Badge>
                </div>

                {/* MCQ: full option list */}
                {r.type === 'mcq' && r.options && Object.keys(r.options).length > 0 ? (
                  <div className="space-y-1 mb-2">
                    {Object.entries(r.options).map(([letter, text]) => {
                      const isRight = letter === r.correct;
                      const isPick = letter === r.user_answer;
                      const cls = isRight
                        ? 'bg-green-100 border-green-300 text-green-900'
                        : isPick
                          ? 'bg-red-100 border-red-300 text-red-900'
                          : 'bg-white border-gray-200 text-gray-700';
                      return (
                        <div key={letter} className={`flex items-start gap-2 p-2 rounded border text-xs ${cls}`}>
                          <span className="font-semibold">{letter}.</span>
                          <span className="flex-1">{text}</span>
                          {isRight && <span className="text-green-700 font-medium shrink-0">✓ Correct</span>}
                          {isPick && !isRight && <span className="text-red-700 font-medium shrink-0">✗ Your answer</span>}
                        </div>
                      );
                    })}
                    {!r.user_answer && (
                      <p className="text-xs text-amber-700 italic">You did not answer this question.</p>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                    <div className="bg-red-50 border border-red-200 rounded p-2">
                      <p className="text-xs font-semibold text-red-700 mb-1">Your answer:</p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{r.user_answer || <em className="text-gray-400">Not answered</em>}</p>
                    </div>
                    {(r.correct || r.model_answer) && (
                      <div className="bg-green-50 border border-green-200 rounded p-2">
                        <p className="text-xs font-semibold text-green-700 mb-1">{r.type === 'mcq' ? 'Correct:' : 'Model answer:'}</p>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{r.correct || r.model_answer}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* AI feedback panel for descriptive answers */}
                {r.type === 'descriptive' && r.status === 'ai_graded' && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded p-3 mt-2 space-y-2" data-testid={`ai-feedback-${i}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-xs font-semibold text-indigo-800 uppercase tracking-wide">AI Feedback · {r.awarded}/{r.marks}</p>
                      {!r.disputed && (
                        <button
                          onClick={() => disputeAiGrade(r.question_id)}
                          disabled={disputing[r.question_id]}
                          className="text-[11px] underline text-indigo-700 hover:text-indigo-900 disabled:opacity-50"
                          data-testid={`dispute-ai-${i}`}
                        >
                          {disputing[r.question_id] ? 'Sending…' : 'Disagree? Send to teacher'}
                        </button>
                      )}
                    </div>
                    {r.ai_well_done && (
                      <div>
                        <p className="text-[11px] font-semibold text-emerald-700">✓ What you did well</p>
                        <p className="text-xs text-gray-800">{r.ai_well_done}</p>
                      </div>
                    )}
                    {r.ai_missing && (
                      <div>
                        <p className="text-[11px] font-semibold text-amber-700">✗ What was missing</p>
                        <p className="text-xs text-gray-800">{r.ai_missing}</p>
                      </div>
                    )}
                    {r.ai_suggestion && (
                      <div>
                        <p className="text-[11px] font-semibold text-blue-700">💡 Tip for next time</p>
                        <p className="text-xs text-gray-800">{r.ai_suggestion}</p>
                      </div>
                    )}
                    {r.disputed && (
                      <p className="text-[11px] text-blue-700 italic">Sent to teacher for review.</p>
                    )}
                  </div>
                )}

                {r.explanation && (
                  <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                    <p className="text-xs font-semibold text-blue-700 mb-0.5">Why:</p>
                    <p className="text-xs text-blue-900">{r.explanation}</p>
                  </div>
                )}
                {r.status === 'needs_review' && (
                  <p className="text-xs text-blue-700 mt-2">
                    <AlertCircle size={12} className="inline mr-1" /> Awaiting teacher review
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StudentMockExams;
